import { useCallback, useEffect, useRef, useState, type ReactNode } from "react"
import { Camera, Flashlight, FlashlightOff, Keyboard, Loader2, Volume2, VolumeX, X } from "lucide-react"
import { cn } from "@/lib/utils"
import { detectInFile, getDetector, type Detector } from "./decoder"
import { campoBridgeNow, hasCampoBridge, runNativeScanLoop } from "@/campo/bridge"

/**
 * Leitor de códigos (Fase 2c, L1) — o mesmo para a contagem e para a receção.
 *
 * Ecrã inteiro, câmara traseira, leitura contínua. Cada código novo vai a
 * `onCode`, que diz o que ele é (a app classifica, não o operador); a resposta
 * aparece numa faixa com as últimas 3 leituras. Vibra e apita (desligável) a
 * cada leitura e não relê o mesmo código durante 2 s.
 *
 * Sem câmara (sem permissão, iframe sem `allow="camera"`, câmara ocupada):
 * plano B, uma foto (`capture`) descodificada; plano C, escrever à mão — sempre
 * à vista.
 *
 * Dentro da app de campo (0.45.0, E2 — `window.flutter_inappwebview`): o
 * leitor nativo (`campoScan`) em vez da câmara da página — ver `NativeScanner`.
 */

export type ScanSource = "camara" | "foto" | "manual"

export interface ScanAction {
  label: string
  run: () => Promise<ScanFeedback | void> | ScanFeedback | void
  /**
   * `false` (0.45.1): não pede resposta já — no leitor nativo da app de campo
   * a leitura seguinte continua (o botão fica nas recentes da página). Por
   * omissão (`true`) o leitor nativo pára e a página mostra os botões.
   */
  blocking?: boolean
}

export interface ScanFeedback {
  tone: "ok" | "warn" | "error" | "info"
  text: string
  /** Ação de um toque (ex.: «Contar mesmo assim», «Trocar»); o que devolver substitui a faixa. */
  action?: ScanAction
  /** Várias ações lado a lado (ex.: «É o EAN deste artigo» · «É a série — contar»); vêm depois de `action`. */
  actions?: ScanAction[]
}

/** Todas as ações de uma resposta (`action` e `actions`). */
export function feedbackActions(feedback: ScanFeedback | null | undefined): ScanAction[] {
  if (!feedback) return []
  return [...(feedback.action ? [feedback.action] : []), ...(feedback.actions ?? [])]
}

/**
 * Os botões das ações de uma resposta, lado a lado (partem linha no
 * telemóvel). Um toque corre a ação; o que devolver vai a `onResult` (substitui
 * a resposta). `tone`: por cima da câmara (`dark`) ou no ecrã (`light`).
 */
export function FeedbackActions({
  feedback,
  onResult,
  tone = "dark",
}: {
  feedback: ScanFeedback
  /**
   * `phase`: `running` no «A tratar…» enquanto a ação corre; `result` com o
   * que a ação devolveu (ou o erro); `empty` se não devolveu nada («Feito.»).
   */
  onResult: (next: ScanFeedback, phase: "running" | "result" | "empty") => void
  tone?: "dark" | "light"
}) {
  const actions = feedbackActions(feedback)
  if (actions.length === 0) return null
  const run = async (action: ScanAction) => {
    onResult({ tone: "info", text: "A tratar…" }, "running")
    try {
      const next = await action.run()
      onResult(next ?? { tone: "ok", text: "Feito." }, next ? "result" : "empty")
      navigator.vibrate?.(40)
    } catch (e) {
      onResult({ tone: "error", text: (e as Error).message }, "result")
    }
  }
  return (
    <div className="mt-1.5 flex flex-wrap gap-2">
      {actions.map((a, i) => (
        <button
          key={`${i}-${a.label}`}
          type="button"
          onClick={() => void run(a)}
          className={cn(
            "min-h-12 min-w-0 max-w-full rounded-md px-4 py-2 text-base font-semibold leading-tight",
            // Várias: repartem a linha e partem para a seguinte num telemóvel estreito.
            actions.length > 1 && "flex-auto",
            tone === "dark" ? "bg-white/90 text-black active:bg-white" : "border bg-background text-foreground active:bg-muted",
          )}
        >
          {a.label}
        </button>
      ))}
    </div>
  )
}

export interface ScannerProps {
  title: string
  /** Contador grande no topo (ex.: «12 / 15»). */
  counter?: ReactNode
  /** Linha de ajuda por baixo do título. */
  hint?: string
  onCode: (code: string, source: ScanSource) => Promise<ScanFeedback> | ScanFeedback
  onClose: () => void
  /** Abre já no «Escrever à mão» (etiqueta ilegível, computador sem câmara). */
  startManual?: boolean
  /** Uma ação de uma resposta devolveu outra: `prev` (a resposta com as ações) passou a `next`. */
  onFeedbackChange?: (prev: ScanFeedback, next: ScanFeedback) => void
}

interface Read {
  id: number
  code: string
  feedback: ScanFeedback | null
}

const REPEAT_MS = 2000
const SCAN_INTERVAL_MS = 120

function cameraProblem(error: unknown): string {
  const name = (error as { name?: string })?.name ?? ""
  if (name === "NotFoundError" || name === "OverconstrainedError") return "Sem câmara neste aparelho — tire uma foto à etiqueta ou escreva à mão."
  if (name === "NotReadableError" || name === "AbortError") return "A câmara está ocupada por outra app — tire uma foto à etiqueta."
  return "Sem acesso à câmara — tire uma foto à etiqueta."
}

function readSoundPref(): boolean {
  try {
    return localStorage.getItem("maisenergia.scanner.sound") !== "off"
  } catch {
    return true
  }
}

let audio: AudioContext | null = null
function beep(ok: boolean) {
  try {
    audio ??= new AudioContext()
    const osc = audio.createOscillator()
    const gain = audio.createGain()
    osc.frequency.value = ok ? 1320 : 330
    gain.gain.value = 0.08
    osc.connect(gain).connect(audio.destination)
    osc.start()
    osc.stop(audio.currentTime + (ok ? 0.07 : 0.18))
  } catch {
    // sem som
  }
}

export function Scanner(props: ScannerProps) {
  // Detetado uma vez por abertura: a ponte não aparece nem desaparece a meio.
  const [native] = useState(hasCampoBridge)
  return native ? <NativeScanner {...props} /> : <CameraScanner {...props} />
}

function CameraScanner({ title, counter, hint, onCode, onClose, startManual = false, onFeedbackChange }: ScannerProps) {
  const video = useRef<HTMLVideoElement>(null)
  const photo = useRef<HTMLInputElement>(null)
  const stream = useRef<MediaStream | null>(null)
  const lastSeen = useRef(new Map<string, number>())
  const seq = useRef(0)
  const [live, setLive] = useState(false)
  const [problem, setProblem] = useState<string | null>(null)
  const [torch, setTorch] = useState<boolean | null>(null)
  const [sound, setSound] = useState(readSoundPref)
  const [reads, setReads] = useState<Read[]>([])
  const [manual, setManual] = useState(startManual)
  const [decodingPhoto, setDecodingPhoto] = useState(false)
  const soundRef = useRef(sound)
  soundRef.current = sound
  const onCodeRef = useRef(onCode)
  onCodeRef.current = onCode
  const onFeedbackChangeRef = useRef(onFeedbackChange)
  onFeedbackChangeRef.current = onFeedbackChange

  /** Sinal de uma leitura (ou de um frame inteiro): vibração e bip. */
  const signal = useCallback((ok: boolean) => {
    navigator.vibrate?.(ok ? 40 : [30, 60, 30])
    if (soundRef.current) beep(ok)
  }, [])

  /**
   * Uma leitura. Devolve a resposta (ou null se foi uma releitura ignorada).
   * Com `quiet`, não dá sinal: quem chama (o frame) dá um só.
   */
  const handle = useCallback(
    async (raw: string, source: ScanSource, quiet = false): Promise<ScanFeedback | null> => {
      const code = raw.trim()
      if (!code) return null
      const at = Date.now()
      if (source === "camara" && at - (lastSeen.current.get(code) ?? 0) < REPEAT_MS) {
        lastSeen.current.set(code, at)
        return null
      }
      lastSeen.current.set(code, at)
      const id = ++seq.current
      setReads((r) => [{ id, code, feedback: null }, ...r].slice(0, 3))
      let feedback: ScanFeedback
      try {
        feedback = await onCodeRef.current(code, source)
      } catch (e) {
        feedback = { tone: "error", text: (e as Error).message }
      }
      if (!quiet) signal(feedback.tone === "ok")
      setReads((r) => r.map((x) => (x.id === id ? { ...x, feedback } : x)))
      return feedback
    },
    [signal],
  )

  /**
   * Um frame com vários códigos (a caixa tem o EAN do produto e a etiqueta com
   * SN): um só sinal — de sucesso se algum contou; o EAN ao lado não dá bip de erro.
   */
  const handleFrame = useCallback(
    async (codes: string[]) => {
      const out = (await Promise.all(codes.map((c) => handle(c, "camara", codes.length > 1)))).filter((f): f is ScanFeedback => f !== null)
      if (codes.length > 1 && out.length > 0) signal(out.some((f) => f.tone === "ok") || out.every((f) => f.tone === "info"))
    },
    [handle, signal],
  )

  // Câmara + leitura contínua. Com o separador escondido (ou o ecrã bloqueado)
  // a câmara desliga-se — não fica a gastar bateria nem com a luz acesa — e
  // volta a ligar-se ao regressar.
  useEffect(() => {
    let stopped = false
    let timer: ReturnType<typeof setTimeout> | undefined
    let detector: Detector | null = null
    let opening = false
    let looping = false

    const loop = async () => {
      if (stopped) return
      const v = video.current
      if (detector && v && v.readyState >= 2 && !document.hidden && stream.current) {
        try {
          const found = await detector.detect(v)
          const codes = [...new Set(found.map((c) => c.rawValue).filter(Boolean))]
          if (codes.length) void handleFrame(codes)
        } catch {
          // um frame mau não pára o leitor
        }
      }
      timer = setTimeout(loop, SCAN_INTERVAL_MS)
    }

    const stopCamera = () => {
      stream.current?.getTracks().forEach((t) => t.stop())
      stream.current = null
      setLive(false)
      setTorch((t) => (t === null ? null : false))
    }

    const openCamera = async (): Promise<boolean> => {
      if (opening || stream.current) return Boolean(stream.current)
      opening = true
      try {
        const s = await navigator.mediaDevices.getUserMedia({
          audio: false,
          video: { facingMode: { ideal: "environment" }, width: { ideal: 1920 }, height: { ideal: 1080 } },
        })
        if (stopped || document.hidden) {
          s.getTracks().forEach((t) => t.stop())
          return false
        }
        stream.current = s
        const v = video.current
        if (v) {
          v.srcObject = s
          await v.play().catch(() => undefined)
        }
        const track = s.getVideoTracks()[0]
        const caps = (track?.getCapabilities?.() ?? {}) as { torch?: boolean }
        setTorch(caps.torch ? false : null)
        setLive(true)
        return true
      } catch (error) {
        setProblem(cameraProblem(error))
        return false
      } finally {
        opening = false
      }
    }

    const start = async () => {
      if (!navigator.mediaDevices?.getUserMedia) {
        setProblem("Este navegador não dá acesso à câmara — tire uma foto à etiqueta.")
        return
      }
      if (!(await openCamera())) return
      try {
        detector = await getDetector()
      } catch {
        setProblem("Não foi possível carregar o leitor de códigos — escreva à mão.")
        return
      }
      if (!looping) {
        looping = true
        void loop()
      }
    }

    const onVisibility = () => {
      if (stopped) return
      if (document.hidden) stopCamera()
      else if (detector) void openCamera()
      else void start() // aberto já escondido: arranca agora
    }
    document.addEventListener("visibilitychange", onVisibility)
    void start()
    return () => {
      stopped = true
      document.removeEventListener("visibilitychange", onVisibility)
      if (timer) clearTimeout(timer)
      stream.current?.getTracks().forEach((t) => t.stop())
      stream.current = null
    }
  }, [handleFrame])

  // Esc fecha (computador).
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => e.key === "Escape" && !manual && onClose()
    window.addEventListener("keydown", onKey)
    return () => window.removeEventListener("keydown", onKey)
  }, [onClose, manual])

  const toggleTorch = async () => {
    const track = stream.current?.getVideoTracks()[0]
    if (!track || torch === null) return
    try {
      await track.applyConstraints({ advanced: [{ torch: !torch } as MediaTrackConstraintSet] })
      setTorch(!torch)
    } catch {
      setTorch(null)
    }
  }

  const toggleSound = () => {
    setSound((s) => {
      try {
        localStorage.setItem("maisenergia.scanner.sound", s ? "off" : "on")
      } catch {
        // preferência só nesta sessão
      }
      return !s
    })
  }

  const fromPhoto = async (file: File | undefined) => {
    if (!file) return
    setDecodingPhoto(true)
    try {
      const codes = await detectInFile(file)
      if (codes.length === 0) {
        setReads((r) => [{ id: ++seq.current, code: "Foto", feedback: { tone: "error" as const, text: "Nenhum código na foto — aproxime-se da etiqueta e tire outra, ou escreva à mão." } }, ...r].slice(0, 3))
        navigator.vibrate?.([30, 60, 30])
      }
      for (const code of codes) await handle(code, "foto")
    } catch {
      setReads((r) => [{ id: ++seq.current, code: "Foto", feedback: { tone: "error" as const, text: "Não foi possível ler a foto — escreva à mão." } }, ...r].slice(0, 3))
    } finally {
      setDecodingPhoto(false)
    }
  }

  // Fechar com gesto: arrastar para baixo a partir do topo.
  const touchY = useRef<number | null>(null)

  return (
    <div
      className="fixed inset-0 z-50 flex flex-col bg-black text-white"
      role="dialog"
      aria-modal="true"
      aria-label={title}
      style={{ paddingTop: "env(safe-area-inset-top)", paddingBottom: "env(safe-area-inset-bottom)" }}
    >
      <div
        className="flex items-center gap-2 px-3 py-2"
        onTouchStart={(e) => (touchY.current = e.touches[0]?.clientY ?? null)}
        onTouchEnd={(e) => {
          const dy = (e.changedTouches[0]?.clientY ?? 0) - (touchY.current ?? 0)
          if (touchY.current !== null && dy > 80) onClose()
          touchY.current = null
        }}
      >
        <div className="min-w-0 flex-1">
          <div className="truncate text-base font-semibold">{title}</div>
          {hint && <div className="truncate text-sm text-white/70">{hint}</div>}
        </div>
        {counter !== undefined && <div className="shrink-0 text-2xl font-bold tabular-nums">{counter}</div>}
        <button
          type="button"
          onClick={onClose}
          aria-label="Fechar o leitor"
          className="flex h-12 w-12 shrink-0 items-center justify-center rounded-full bg-white/15 active:bg-white/30"
        >
          <X className="h-6 w-6" />
        </button>
      </div>

      <div className="relative min-h-0 flex-1 overflow-hidden">
        <video ref={video} playsInline muted autoPlay className={cn("h-full w-full object-cover", !live && "hidden")} />
        {live && (
          <div className="pointer-events-none absolute inset-0 flex items-center justify-center">
            <div className="relative h-[38%] w-[82%] max-w-md rounded-xl border-2 border-white/80 shadow-[0_0_0_9999px_rgba(0,0,0,0.35)]">
              <div className="absolute inset-x-3 top-1/2 h-0.5 -translate-y-1/2 bg-red-500/80" />
            </div>
          </div>
        )}
        {!live && !problem && (
          <div className="flex h-full items-center justify-center text-white/80">
            <Loader2 className="mr-2 h-5 w-5 animate-spin" /> A abrir a câmara…
          </div>
        )}
        {problem && (
          <div className="flex h-full flex-col items-center justify-center gap-4 p-6 text-center">
            <p className="text-base">{problem}</p>
            <button
              type="button"
              onClick={() => photo.current?.click()}
              className="flex h-14 items-center gap-2 rounded-xl bg-white px-6 text-base font-semibold text-black active:bg-white/80"
            >
              {decodingPhoto ? <Loader2 className="h-5 w-5 animate-spin" /> : <Camera className="h-5 w-5" />} Tirar foto à etiqueta
            </button>
          </div>
        )}

        {/* As últimas 3 leituras, por cima da câmara. */}
        <ul className="absolute inset-x-2 top-2 space-y-1.5" aria-live="polite">
          {reads.map((r) => (
            <li
              key={r.id}
              className={cn(
                "rounded-lg px-3 py-2 text-sm shadow backdrop-blur",
                !r.feedback && "bg-black/60",
                r.feedback?.tone === "ok" && "bg-emerald-600/90",
                r.feedback?.tone === "warn" && "bg-amber-500/95 text-black",
                r.feedback?.tone === "error" && "bg-red-600/90",
                r.feedback?.tone === "info" && "bg-sky-700/90",
              )}
            >
              <div className="flex items-center gap-2">
                <span className="min-w-0 flex-1 truncate font-mono text-[15px] font-semibold">{r.code}</span>
                {!r.feedback && <Loader2 className="h-4 w-4 shrink-0 animate-spin" />}
              </div>
              {r.feedback && <div className="leading-snug">{r.feedback.text}</div>}
              {r.feedback && feedbackActions(r.feedback).length > 0 && (
                <FeedbackActions
                  feedback={r.feedback}
                  onResult={(next, phase) => {
                    setReads((all) => all.map((x) => (x.id === r.id ? { ...x, feedback: next } : x)))
                    // O ecrã (as recentes) só fica a saber de um resultado, não do «A tratar…» nem do «Feito.».
                    if (phase === "result" && r.feedback) onFeedbackChangeRef.current?.(r.feedback, next)
                  }}
                />
              )}
            </li>
          ))}
        </ul>
      </div>

      <div className="grid grid-cols-3 gap-2 p-3">
        <button
          type="button"
          onClick={toggleTorch}
          disabled={torch === null}
          aria-pressed={torch === true}
          className="flex h-14 flex-col items-center justify-center rounded-xl bg-white/15 text-xs disabled:opacity-40 active:bg-white/30"
        >
          {torch ? <FlashlightOff className="h-5 w-5" /> : <Flashlight className="h-5 w-5" />}
          Lanterna
        </button>
        <button
          type="button"
          onClick={() => photo.current?.click()}
          className="flex h-14 flex-col items-center justify-center rounded-xl bg-white/15 text-xs active:bg-white/30"
        >
          {decodingPhoto ? <Loader2 className="h-5 w-5 animate-spin" /> : <Camera className="h-5 w-5" />}
          Foto
        </button>
        <button
          type="button"
          onClick={() => setManual(true)}
          className="flex h-14 flex-col items-center justify-center rounded-xl bg-white text-xs font-semibold text-black active:bg-white/80"
        >
          <Keyboard className="h-5 w-5" />
          Escrever à mão
        </button>
        <button
          type="button"
          onClick={toggleSound}
          aria-pressed={sound}
          className="col-span-3 flex h-10 items-center justify-center gap-2 text-sm text-white/70"
        >
          {sound ? <Volume2 className="h-4 w-4" /> : <VolumeX className="h-4 w-4" />}
          {sound ? "Som ligado" : "Som desligado"}
        </button>
      </div>

      <input
        ref={photo}
        type="file"
        accept="image/*"
        capture="environment"
        className="hidden"
        onChange={(e) => {
          void fromPhoto(e.target.files?.[0])
          e.target.value = ""
        }}
      />

      {manual && (
        <ManualEntry
          onCancel={() => setManual(false)}
          onSubmit={(code) => {
            setManual(false)
            void handle(code, "manual")
          }}
        />
      )}
    </div>
  )
}

/** O contador grande, em texto (para o leitor nativo). */
function counterText(counter: ReactNode): string | undefined {
  return typeof counter === "string" || typeof counter === "number" ? String(counter).trim() || undefined : undefined
}

/**
 * Leitor nativo da app de campo (E2): chama `campoScan` em ciclo — abre com o
 * título e a ajuda, recebe o código, passa-o ao `onCode` de sempre e volta a
 * abrir com a resposta (`feedback`). Pára quando quem lê cancela, ou quando a
 * resposta pede uma escolha (botões): o leitor fecha e a página mostra-a nas
 * recentes, com os botões, como na web. `startManual`: a folha «Escrever o
 * código» da página (uma leitura, e fecha).
 *
 * Enquanto o leitor nativo está por cima, a página só mostra uma faixa no
 * fundo («A ler na app…»), por baixo dos avisos (ativar a série) que uma
 * leitura pode abrir.
 */
function NativeScanner({ title, counter, hint, onCode, onClose, startManual = false }: ScannerProps) {
  const latest = useRef({ title, counter, hint, onCode, onClose })
  latest.current = { title, counter, hint, onCode, onClose }
  const [problem, setProblem] = useState<string | null>(null)

  useEffect(() => {
    if (startManual) return
    const bridge = campoBridgeNow()
    if (!bridge) {
      setProblem("A app de campo não respondeu — feche e abra o leitor outra vez.")
      return
    }
    let stopped = false
    void runNativeScanLoop({
      scan: (input) => bridge.scan(input),
      onCode: (code) => latest.current.onCode(code, "camara"),
      input: () => ({ title: latest.current.title, hint: latest.current.hint, counter: counterText(latest.current.counter) }),
      stopped: () => stopped,
      // O artigo que a leitura abriu (título, contador) entra no ecrã antes de reabrir o leitor.
      settle: () => new Promise((r) => setTimeout(r, 50)),
    }).then((end) => {
      if (stopped) return
      if (end.reason === "error") setProblem(`O leitor da app falhou: ${(end.error as Error)?.message ?? String(end.error)}`)
      else latest.current.onClose()
    })
    return () => {
      stopped = true
    }
  }, [startManual])

  if (startManual) {
    return (
      <div className="fixed inset-0 z-50 bg-black/40">
        <ManualEntry
          onCancel={onClose}
          onSubmit={(code) => {
            void Promise.resolve(onCode(code, "manual"))
              .catch(() => undefined)
              .finally(onClose)
          }}
        />
      </div>
    )
  }
  return (
    <div
      className="fixed inset-x-0 bottom-0 z-40 flex items-center gap-3 border-t bg-background/95 px-3 py-3 backdrop-blur"
      style={{ paddingBottom: "calc(0.75rem + env(safe-area-inset-bottom))" }}
      role="status"
      aria-live="polite"
    >
      {problem ? <p className="min-w-0 flex-1 text-sm text-red-700">{problem}</p> : (
        <p className="flex min-w-0 flex-1 items-center gap-2 text-sm text-muted-foreground">
          <Loader2 className="h-4 w-4 shrink-0 animate-spin" /> A ler na app…
        </p>
      )}
      <button type="button" onClick={onClose} className="h-11 shrink-0 rounded-xl border px-4 text-sm font-medium active:bg-muted">
        Fechar
      </button>
    </div>
  )
}

/** Folha inferior para escrever um código (etiqueta ilegível). */
function ManualEntry({ onSubmit, onCancel }: { onSubmit: (code: string) => void; onCancel: () => void }) {
  const [value, setValue] = useState("")
  return (
    <div className="absolute inset-0 z-10 flex items-end bg-black/60" onClick={onCancel}>
      <form
        className="w-full space-y-3 rounded-t-2xl bg-background p-4 text-foreground"
        style={{ paddingBottom: "calc(1rem + env(safe-area-inset-bottom))" }}
        onClick={(e) => e.stopPropagation()}
        onSubmit={(e) => {
          e.preventDefault()
          if (value.trim()) onSubmit(value.trim())
        }}
      >
        <div className="mx-auto h-1.5 w-10 rounded-full bg-muted-foreground/30" />
        <label className="block text-base font-semibold" htmlFor="scanner-manual">
          Escrever o código
        </label>
        <input
          id="scanner-manual"
          autoFocus
          autoCapitalize="characters"
          autoComplete="off"
          autoCorrect="off"
          spellCheck={false}
          value={value}
          onChange={(e) => setValue(e.target.value)}
          className="h-12 w-full rounded-lg border bg-background px-3 font-mono text-lg"
          placeholder="Nº de série"
        />
        <div className="grid grid-cols-2 gap-2">
          <button type="button" onClick={onCancel} className="h-12 rounded-lg border text-base font-medium">
            Cancelar
          </button>
          <button type="submit" disabled={!value.trim()} className="h-12 rounded-lg bg-primary text-base font-semibold text-primary-foreground disabled:opacity-50">
            Adicionar
          </button>
        </div>
      </form>
    </div>
  )
}
